import React from 'react';
import { useFormState } from 'react-final-form';
import { Button } from '@tcl-boron-prefabs/button';
import {
  getNeuroExtenuatingCircumstancesDropdownOptions,
  getNeuroNumberFamilyMembersDropdownOptions,
  NeuroFinancialAssistanceContent,
  TEXT_FIELD_CHARACTER_LIMIT,
  NeuroRelationshipToPatient,
  neuroFinancialAssistanceV2Fields,
  NeuroFinancialAssistanceV2PatientConsent,
  NeuroFinancialAssistanceV2PatientRepresentativeConsent,
  NeuroFinancialAssistanceV2TempusRepresentativeConsent,
  NeuroFinancialAssistanceV2Disclaimer,
  getNeuroRelationshipToPatientDropdownOptions,
} from '@tempus/patient-forms-service-shared';
import { FormProps } from '../../forms';
import FormAlert, { AlertStatus } from '../../shared/FormAlert';
import FormBannerError from '../../shared/FormBannerError';
import FormTable from '../../shared/FormTable';
import { CHARACTER_LIMIT_WARNING, REQUIRED_FIELD_WARNING } from '../../shared/validation-utils';
import styles from '../../shared/styles.module.css';
import { Typography } from '@mui/material';
import UploadZone, { CachedFile } from '../../../../portal/components/UploadZone';
import getBase64File from '../../../../portal/utils/getBase64File';
import { combineFormConfigs } from '../../shared/utils';
import { EXCLUDED_FORM_FIELDS } from '../../shared/PatientStandaloneFormAuthenticationFlow';

export const neuroFinancialAssistancePatientInfoFields = [
  {
    name: 'grossAnnualHouseholdIncome',
    props: {
      type: 'number',
    },
    validate: (value: any | undefined): string | undefined => {
      if (!value) {
        return REQUIRED_FIELD_WARNING;
      }
    },
  },
  {
    name: 'numberOfFamilyMembersInHousehold',
    props: {
      dropdownOptions: getNeuroNumberFamilyMembersDropdownOptions(),
    },
    validate: (value: any | undefined): string | undefined => {
      if (!value) {
        return REQUIRED_FIELD_WARNING;
      }
    },
  },
  {
    name: 'extenuatingCircumstances',
    props: {
      checkboxOptions: getNeuroExtenuatingCircumstancesDropdownOptions(),
    },
  },
];

export const neuroFinancialAssistanceConsentFields = [
  {
    name: 'relationshipToPatient',
    props: {
      placeholder: 'Select',
      dropdownOptions: getNeuroRelationshipToPatientDropdownOptions(),
    },
    validate: (value: any | undefined): string | undefined => {
      if (!value) {
        return REQUIRED_FIELD_WARNING;
      }
    },
  },
  {
    conditionalValueConfig: {
      fieldNameToCheck: 'relationshipToPatient',
      valueToCheck: NeuroRelationshipToPatient.TEMPUS_REPRESENTATIVE,
      conditionalLogic: (value: any, conditionalValue: string) => {
        return value === conditionalValue;
      },
    },
    name: 'tempusRepresentativeFirstName',
    validate: (value: any | undefined): string | undefined => {
      if (!value) {
        return REQUIRED_FIELD_WARNING;
      } else if (String(value).length > TEXT_FIELD_CHARACTER_LIMIT) {
        return CHARACTER_LIMIT_WARNING;
      }
    },
  },
  {
    conditionalValueConfig: {
      fieldNameToCheck: 'relationshipToPatient',
      valueToCheck: NeuroRelationshipToPatient.TEMPUS_REPRESENTATIVE,
      conditionalLogic: (value: any, conditionalValue: string) => {
        return value === conditionalValue;
      },
    },
    name: 'tempusRepresentativeLastName',
    validate: (value: any | undefined): string | undefined => {
      if (!value) {
        return REQUIRED_FIELD_WARNING;
      } else if (String(value).length > TEXT_FIELD_CHARACTER_LIMIT) {
        return CHARACTER_LIMIT_WARNING;
      }
    },
  },
  {
    conditionalValueConfig: {
      fieldNameToCheck: 'relationshipToPatient',
      valueToCheck: [
        NeuroRelationshipToPatient.TEMPUS_REPRESENTATIVE,
        NeuroRelationshipToPatient.PATIENT_REPRESENTATIVE,
      ],
      conditionalLogic: (value: any, conditionalValue: NeuroRelationshipToPatient[]) => {
        return conditionalValue.includes(value as NeuroRelationshipToPatient);
      },
    },
    name: 'representativeFirstName',
    validate: (value: any | undefined, allValues: Record<string, any>): string | undefined => {
      if (!value && allValues.relationshipToPatient === NeuroRelationshipToPatient.PATIENT_REPRESENTATIVE) {
        return REQUIRED_FIELD_WARNING;
      } else if (String(value).length > TEXT_FIELD_CHARACTER_LIMIT) {
        return CHARACTER_LIMIT_WARNING;
      }
    },
  },
  {
    conditionalValueConfig: {
      fieldNameToCheck: 'relationshipToPatient',
      valueToCheck: [
        NeuroRelationshipToPatient.TEMPUS_REPRESENTATIVE,
        NeuroRelationshipToPatient.PATIENT_REPRESENTATIVE,
      ],
      conditionalLogic: (value: any, conditionalValue: NeuroRelationshipToPatient[]) => {
        return conditionalValue.includes(value as NeuroRelationshipToPatient);
      },
    },
    name: 'representativeLastName',
    validate: (value: any | undefined, allValues: Record<string, any>): string | undefined => {
      if (!value && allValues.relationshipToPatient === NeuroRelationshipToPatient.PATIENT_REPRESENTATIVE) {
        return REQUIRED_FIELD_WARNING;
      } else if (String(value).length > TEXT_FIELD_CHARACTER_LIMIT) {
        return CHARACTER_LIMIT_WARNING;
      }
    },
  },
  {
    conditionalValueConfig: {
      fieldNameToCheck: 'relationshipToPatient',
      valueToCheck: [
        NeuroRelationshipToPatient.TEMPUS_REPRESENTATIVE,
        NeuroRelationshipToPatient.PATIENT_REPRESENTATIVE,
      ],
      conditionalLogic: (value: any, conditionalValue: NeuroRelationshipToPatient[]) => {
        return conditionalValue.includes(value as NeuroRelationshipToPatient);
      },
    },
    name: 'representativeRelationshipToPatient',
    validate: (value: any | undefined): string | undefined => {
      if (String(value).length > TEXT_FIELD_CHARACTER_LIMIT) {
        return CHARACTER_LIMIT_WARNING;
      }
    },
  },
  {
    conditionalValueConfig: {
      fieldNameToCheck: 'relationshipToPatient',
      valueToCheck: [
        NeuroRelationshipToPatient.TEMPUS_REPRESENTATIVE,
        NeuroRelationshipToPatient.PATIENT_REPRESENTATIVE,
      ],
      conditionalLogic: (value: any, conditionalValue: NeuroRelationshipToPatient[]) => {
        return conditionalValue.includes(value as NeuroRelationshipToPatient);
      },
    },
    name: 'representativeEmail',
    validate: (value: any | undefined): string | undefined => {
      if (String(value).length > TEXT_FIELD_CHARACTER_LIMIT) {
        return CHARACTER_LIMIT_WARNING;
      }
    },
  },
  {
    conditionalValueConfig: {
      fieldNameToCheck: 'relationshipToPatient',
      valueToCheck: [
        NeuroRelationshipToPatient.TEMPUS_REPRESENTATIVE,
        NeuroRelationshipToPatient.PATIENT_REPRESENTATIVE,
      ],
      conditionalLogic: (value: any, conditionalValue: NeuroRelationshipToPatient[]) => {
        return conditionalValue.includes(value as NeuroRelationshipToPatient);
      },
    },
    name: 'representativePhoneNumber',
    validate: (value: any | undefined): string | undefined => {
      if (String(value).length > TEXT_FIELD_CHARACTER_LIMIT) {
        return CHARACTER_LIMIT_WARNING;
      }
    },
  },
];

export const neuroFinancialAssistanceSignatureFields = [
  {
    name: 'signature',
    props: { placeholder: 'Sign here' },
    validate: (value: any | undefined) => {
      if (!value) return REQUIRED_FIELD_WARNING;
    },
  },
];

type NeuroFinancialAssistancePropsV2 = FormProps & {
  cachedFile?: CachedFile;
  setCachedFile: (file: CachedFile | undefined) => void;
};

const NeuroFinancialAssistanceFormV2: React.FC<NeuroFinancialAssistancePropsV2> = (props) => {
  const { cachedFile, setCachedFile } = props;
  const formState = useFormState();

  const formConfigPatientInfo = combineFormConfigs(
    neuroFinancialAssistancePatientInfoFields,
    neuroFinancialAssistanceV2Fields.filter(
      (field) =>
        neuroFinancialAssistancePatientInfoFields.some((sectionField) => field.name === sectionField.name) &&
        !EXCLUDED_FORM_FIELDS.includes(field.name),
    ),
  );

  const formConfigConsent = combineFormConfigs(
    neuroFinancialAssistanceConsentFields,
    neuroFinancialAssistanceV2Fields.filter(
      (field) =>
        neuroFinancialAssistanceConsentFields.some((sectionField) => field.name === sectionField.name) &&
        !EXCLUDED_FORM_FIELDS.includes(field.name),
    ),
  );

  const formConfigSignature = combineFormConfigs(
    neuroFinancialAssistanceSignatureFields,
    neuroFinancialAssistanceV2Fields.filter(
      (field) =>
        neuroFinancialAssistanceSignatureFields.some((sectionField) => field.name === sectionField.name) &&
        !EXCLUDED_FORM_FIELDS.includes(field.name),
    ),
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '16px' }}>
      {formState.submitFailed ? <FormBannerError /> : undefined}
      <NeuroFinancialAssistanceContent />
      <FormTable fields={formConfigPatientInfo} />
      <FormTable fields={formConfigConsent} />
      {formState.values.relationshipToPatient === NeuroRelationshipToPatient.PATIENT && (
        <NeuroFinancialAssistanceV2PatientConsent />
      )}
      {formState.values.relationshipToPatient === NeuroRelationshipToPatient.PATIENT_REPRESENTATIVE && (
        <NeuroFinancialAssistanceV2PatientRepresentativeConsent />
      )}
      {formState.values.relationshipToPatient === NeuroRelationshipToPatient.TEMPUS_REPRESENTATIVE && (
        <NeuroFinancialAssistanceV2TempusRepresentativeConsent />
      )}
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <FormTable fields={formConfigSignature} />
        <NeuroFinancialAssistanceV2Disclaimer />
      </div>
      <Typography variant="h3">
        Acceptable proof of household income
        <Typography variant="body2" color="textSecondary">
          Upload your most recent Form 1040, W-2, or pay stub to verify your income.
        </Typography>
      </Typography>
      <UploadZone
        docPreview={cachedFile?.content}
        docType={cachedFile?.type}
        setFile={async (file) => {
          if (file) {
            const base64File = (await getBase64File(file)) as string;
            setCachedFile({
              name: file.name,
              type: file.type,
              content: base64File,
            } as CachedFile);
          } else {
            setCachedFile(undefined);
          }
        }}
        width="100%"
      />
      {cachedFile?.content ? (
        <Button buttonType="tertiary" small onClick={() => setCachedFile(undefined)} ariaLabel="Remove">
          Remove
        </Button>
      ) : null}
      <div className={styles.footer}>
        {props.handleNavigationBack && (
          <div className={styles.backButton}>
            <Button
              ariaLabel="neuro-financial-assistance-back"
              type="button"
              buttonType="secondary"
              onClick={props.handleNavigationBack}
              disabled={formState.submitting}
            >
              Back
            </Button>
          </div>
        )}
        {props.showAlert && (
          <FormAlert
            setShowAlert={props.setShowAlert}
            message={''}
            title={'Successfully submitted your Financial Assistance. Thank you!'}
            status={AlertStatus.SUCCESS}
          />
        )}
        <Button
          loading={formState.submitting}
          ariaLabel="neuro-financial-assistance-submit"
          type="submit"
          disabled={!formState.valid || !cachedFile?.content || formState.submitting}
        >
          Submit
        </Button>
      </div>
    </div>
  );
};

export default NeuroFinancialAssistanceFormV2;
